import { IncomeCalculationDocument } from '@snapptinc/fraud-platform';
import { format } from 'date-fns';
import { IncomeVerificationEnum } from 'src/ts/enums/session';
import { IncomeVerification } from '../../ts/interfaces/incomeVerification';
import { CalculationDocument, defaultStateIVFields, eligibleDocuments } from './constants';

export const formatApiDate = (dateStr: string): string => {
  const date = new Date(dateStr);
  return isNaN(date.getTime()) ? dateStr : format(date, 'MM/dd/yyyy');
};

/**
 * Converts the shape of the income verification document object from the shape the API returns
 * to the shape the client expects.
 *
 * @param {IncomeCalculationDocument} objectToConvert
 * @returns {IncomeVerification}
 */
export function convertToIncomeVerificationType(
  objectToConvert: IncomeCalculationDocument
): IncomeVerification {
  const incomingObject: IncomeCalculationDocument = { ...objectToConvert };
  // This map helps with the conversion because we can't check the type keys at runtime...
  const mapFromApiToClient: Partial<
    Record<keyof IncomeCalculationDocument, keyof IncomeVerification>
  > = {
    applicant_name: 'recipientName',
    document_end_date: 'payPeriodEndDate',
    document_id: 'id',
    document_start_date: 'payPeriodStartDate',
    gross_income: 'grossPay',
    income_source: 'incomeSource'
  };
  const result = {};

  for (const key in mapFromApiToClient) {
    const castKey = key as keyof IncomeCalculationDocument;
    const mappedKey = mapFromApiToClient[castKey];
    if (mappedKey) {
      Object.assign(result, {
        [mappedKey]: incomingObject[castKey]
      });
    }
  }
  return result as IncomeVerification;
}

export const getIncomeDocumentsFromSession = (
  incomeVerificationReviewId: string
): IncomeVerification[] => {
  const documentsFromSession = window.sessionStorage.getItem(
    `${IncomeVerificationEnum.INCOME_VERIFICATION_DOCUMENTS}${incomeVerificationReviewId}`
  );
  return documentsFromSession === null ? [] : JSON.parse(documentsFromSession);
};

const parseCurrencyAmount = (value: string | number | null | undefined): number => {
  if (typeof value === 'number') return isFinite(value) ? value : 0;
  const parsed = parseFloat((value ?? '').toString().replace(/[$,]/g, '') || '0');
  return isFinite(parsed) ? parsed : 0;
};

export const buildIncomeCalcMetadata = (
  basePay: string | number | null | undefined,
  hasNonRecurringIncome: boolean | undefined
) => ({
  pay_breakdown: { base: parseCurrencyAmount(basePay) },
  has_non_recurring_income: hasNonRecurringIncome === true
});

export const parseIncomeCalculationDocuments = (documents: IncomeVerification[]) =>
  documents.map((document) => ({
    document_id: document.id,
    applicant_name: document.recipientName || '',
    document_start_date: document.payPeriodStartDate || '',
    document_end_date: document.payPeriodEndDate || '',
    gross_income: parseCurrencyAmount(document.grossPay),
    income_source: document.incomeSource || '',
    metadata: buildIncomeCalcMetadata(document.basePayIncomeAmount, document.hasNonRecurringIncome)
  }));

export const enrichDocumentWithCalculation = (
  document: IncomeVerification,
  calcDoc: CalculationDocument | undefined
): IncomeVerification => {
  if (!calcDoc) return document;
  return {
    ...document,
    recipientName: document.recipientName || calcDoc.applicant_name || '',
    grossPay: document.grossPay || String(calcDoc.gross_income ?? ''),
    payPeriodStartDate:
      document.payPeriodStartDate ||
      (calcDoc.document_start_date ? formatApiDate(calcDoc.document_start_date) : ''),
    payPeriodEndDate:
      document.payPeriodEndDate ||
      (calcDoc.document_end_date ? formatApiDate(calcDoc.document_end_date) : ''),
    payPeriodDateRange:
      document.payPeriodDateRange ||
      (calcDoc.document_start_date && calcDoc.document_end_date
        ? `${formatApiDate(calcDoc.document_start_date)} - ${formatApiDate(
            calcDoc.document_end_date
          )}`
        : ''),
    incomeSource: document.incomeSource || calcDoc.income_source || '',
    hasNonRecurringIncome:
      document.hasNonRecurringIncome ?? calcDoc.has_non_recurring_income ?? false,
    basePayIncomeAmount:
      document.basePayIncomeAmount ||
      (calcDoc.base_pay_income_amount != null ? String(calcDoc.base_pay_income_amount) : '')
  };
};

export const getIsIVDocumentEmpty = (document: IncomeVerification): boolean => {
  return (
    eligibleDocuments.includes(document.type) &&
    Object.keys(defaultStateIVFields).every((key) => !document[key as keyof IncomeVerification])
  );
};

// Mutates in place: rapid debounced saves must see each other within one render.
// Match by id, never by data.index — the carousel re-indexes `index` over its
// isAutomated-filtered view, so a positional write lands on an unrelated document.
export const applyDocumentEdit = (
  documents: IncomeVerification[],
  data: IncomeVerification
): void => {
  const docIndex = documents.findIndex((doc) => doc.id === data.id);

  if (docIndex !== -1) {
    documents[docIndex] = data;
  }
};

export const clearIVFormFields = (
  setFormValue: (key: keyof typeof defaultStateIVFields, value: string | boolean) => void
) => {
  Object.entries(defaultStateIVFields).forEach(([key, value]) => {
    setFormValue(key as keyof typeof defaultStateIVFields, value);
  });
};
